import { computed, onScopeDispose, ref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { getRecognitionEmployees } from '@/queries/recognition';
import { scopePayload } from '@/pages/recognition/constants';

// Headcount for a scope: one debounced request per settled scope, cached by vue-query under the
// sorted scope, so going back to an audience doesn't recount it. The company total comes from
// `savedTotal` when the form passes one (0 means its load failed), else one fetch.

// Sorted so the same audience always has the same query key; null means everyone (or not chosen).
const normalise = scope => {
  if (!scope || scope.division === null) {
    return null;
  }
  const { scope: payload } = scopePayload(scope);
  const keys = Object.keys(payload).sort();
  return keys.length ? Object.fromEntries(keys.map(k => [k, [...payload[k]].sort()])) : null;
};
const keyOf = scope => JSON.stringify(scope);
const length = rows => rows.length;
// Headcounts don't move while a form is open, so a counted audience is never refetched.
const countQuery = scope => ({ ...getRecognitionEmployees(scope), select: length, staleTime: Infinity });

export default function useAudienceCount(scopeRef, { savedTotal, debounceMs = 300 } = {}) {
  const queryClient = useQueryClient();
  const target = computed(() => normalise(scopeRef.value));
  const settled = ref(null);
  let timer = null;

  watch(
    () => keyOf(target.value),
    () => {
      const next = target.value;
      clearTimeout(timer);
      if (!next || queryClient.getQueryData(getRecognitionEmployees(next).queryKey)) {
        settled.value = next;
        return;
      }
      timer = setTimeout(() => (settled.value = next), debounceMs);
    },
    { immediate: true },
  );
  onScopeDispose(() => clearTimeout(timer));

  const totalQuery = useQuery({ ...countQuery(null), enabled: !savedTotal });
  const scopedQuery = useQuery(computed(() => ({ ...countQuery(settled.value), enabled: settled.value !== null })));

  const total = computed(() => (savedTotal ? savedTotal.value || null : totalQuery.data.value ?? null));
  const isEmpty = computed(() => target.value === null);
  const pending = computed(() => keyOf(target.value) !== keyOf(settled.value));
  const count = computed(() => {
    if (isEmpty.value) {
      return total.value;
    }
    return pending.value ? null : scopedQuery.data.value ?? null;
  });
  const loading = computed(() => {
    if (isEmpty.value) {
      return !savedTotal && totalQuery.isLoading.value;
    }
    return pending.value || scopedQuery.isLoading.value;
  });

  return { count, total, loading };
}
